import React from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { MapPin, Building2, Crown, Shield, User } from 'lucide-react';
import { useBranch } from '@/contexts/BranchContext';
import { cn } from '@/lib/utils';

interface BranchSelectorProps {
  className?: string;
  variant?: 'default' | 'compact' | 'header';
}

const roleIcons = {
  super_admin: Crown,
  admin: Crown,
  branch_admin: Shield,
  branch_staff: User,
  staff: User,
  customer: User,
};

const roleLabels = {
  super_admin: 'Super Admin',
  admin: 'Admin',
  branch_admin: 'Branch Admin',
  branch_staff: 'Staff',
  staff: 'Staff',
  customer: 'Customer',
};

const roleColors = {
  super_admin: 'bg-purple-100 text-purple-800 border-purple-200',
  admin: 'bg-purple-100 text-purple-800 border-purple-200',
  branch_admin: 'bg-blue-100 text-blue-800 border-blue-200',
  branch_staff: 'bg-green-100 text-green-800 border-green-200',
  staff: 'bg-green-100 text-green-800 border-green-200',
  customer: 'bg-gray-100 text-gray-800 border-gray-200',
};

export function BranchSelector({ className, variant = 'default' }: BranchSelectorProps) {
  const {
    currentBranch,
    accessibleBranches,
    isLoadingBranches,
    switchBranch,
    isSuperAdmin,
  } = useBranch();

  const handleBranchChange = async (branchId: string) => {
    await switchBranch(branchId);
  };

  if (isLoadingBranches) {
    return (
      <div className={cn('flex items-center space-x-2', className)}>
        <Building2 className="h-4 w-4 animate-pulse" />
        <div className="h-4 w-24 bg-gray-200 rounded animate-pulse" />
      </div>
    );
  }

  if (accessibleBranches.length === 0) {
    return (
      <div className={cn('flex items-center space-x-2 text-gray-500', className)}>
        <Building2 className="h-4 w-4" />
        <span className="text-sm">No branches assigned</span>
      </div>
    );
  }

  if (variant === 'compact') {
    return (
      <Select value={currentBranch?.id || ''} onValueChange={handleBranchChange}>
        <SelectTrigger className={cn('w-40', className)}>
          <SelectValue>
            <div className="flex items-center space-x-2">
              <Building2 className="h-4 w-4" />
              <span className="truncate">{currentBranch?.code || 'Select Branch'}</span>
            </div>
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {accessibleBranches.map((branchAccess, index) => {
            const RoleIcon = roleIcons[branchAccess.role as keyof typeof roleIcons];
            return (
              <SelectItem key={`${branchAccess.branch_id}-${index}`} value={branchAccess.branch_id}>
                <div className="flex items-center space-x-2">
                  <RoleIcon className="h-3 w-3" />
                  <span>{branchAccess.branch_code}</span>
                </div>
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>
    );
  }

  if (variant === 'header') {
    return (
      <div className={cn('flex items-center space-x-1 sm:space-x-3', className)}>
        <div className="flex items-center space-x-1 sm:space-x-2">
          <MapPin className="h-4 w-4 text-gray-400 sm:text-gray-500" />
          <Select value={currentBranch?.id || ''} onValueChange={handleBranchChange}>
            <SelectTrigger className="border-0 shadow-none p-0 h-auto focus:ring-0">
              <SelectValue>
                <div className="flex items-center">
                  <span className="font-medium text-xs sm:text-sm truncate max-w-[70px] xs:max-w-[100px] sm:max-w-none">
                    {currentBranch?.name || 'Select Branch'}
                  </span>
                </div>
              </SelectValue>
            </SelectTrigger>
            <SelectContent align="end" className="w-[calc(100vw-2rem)] sm:w-80 max-w-sm">
              <div className="p-2 border-b">
                <h4 className="font-medium text-sm text-gray-900">Switch Branch</h4>
                <p className="text-xs text-gray-500">
                  {isSuperAdmin ? 'All branches available' : `${accessibleBranches.length} accessible branches`}
                </p>
              </div>
              {accessibleBranches.map((branchAccess, index) => {
                const RoleIcon = roleIcons[branchAccess.role as keyof typeof roleIcons];
                const isSelected = branchAccess.branch_id === currentBranch?.id;

                return (
                  <SelectItem key={`${branchAccess.branch_id}-${index}`} value={branchAccess.branch_id}>
                    <div className="flex items-center justify-between w-full">
                      <div className="flex items-center space-x-3">
                        <RoleIcon className="h-4 w-4 text-gray-500" />
                        <div>
                          <div className="flex items-center space-x-2">
                            <span className="font-medium">{branchAccess.branch_name}</span>
                            {branchAccess.is_primary && (
                              <Badge variant="secondary" className="text-xs">
                                Primary
                              </Badge>
                            )}
                            {isSelected && (
                              <Badge variant="default" className="text-xs">
                                Current
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center space-x-2 mt-1">
                            <span className="text-xs text-gray-500">{branchAccess.branch_code}</span>
                            <Badge 
                              variant="outline" 
                              className={cn('text-xs', roleColors[branchAccess.role as keyof typeof roleColors])}
                            >
                              {roleLabels[branchAccess.role as keyof typeof roleLabels]}
                            </Badge>
                          </div>
                        </div>
                      </div>
                    </div>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>
        
        {currentBranch && (
          <div className="flex items-center space-x-2">
            <Badge variant="outline" className="text-xs">
              {currentBranch.code}
            </Badge>
            {isSuperAdmin && (
              <Badge variant="secondary" className="text-xs">
                <Crown className="h-3 w-3 mr-1" />
                Super Admin
              </Badge>
            )}
          </div>
        )}
      </div>
    );
  }

  // Default variant
  return (
    <div className={cn('space-y-2', className)}>
      <label className="text-sm font-medium text-gray-700">Current Branch</label>
      <Select value={currentBranch?.id || ''} onValueChange={handleBranchChange}>
        <SelectTrigger>
          <SelectValue placeholder="Select a branch">
            {currentBranch && (
              <div className="flex items-center space-x-2">
                <Building2 className="h-4 w-4" />
                <span>{currentBranch.name}</span>
                <Badge variant="outline" className="text-xs">
                  {currentBranch.code}
                </Badge>
              </div>
            )}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          <div className="p-2 border-b">
            <h4 className="font-medium text-sm text-gray-900">Available Branches</h4>
            <p className="text-xs text-gray-500">
              Select a branch to switch your context
            </p>
          </div>
          {accessibleBranches.map((branchAccess, index) => {
            const RoleIcon = roleIcons[branchAccess.role as keyof typeof roleIcons];

            return (
              <SelectItem key={`${branchAccess.branch_id}-${index}`} value={branchAccess.branch_id}>
                <div className="flex items-center justify-between w-full">
                  <div className="flex items-center space-x-3">
                    <RoleIcon className="h-4 w-4 text-gray-500" />
                    <div>
                      <div className="flex items-center space-x-2">
                        <span className="font-medium">{branchAccess.branch_name}</span>
                        {branchAccess.is_primary && (
                          <Badge variant="secondary" className="text-xs">
                            Primary
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center space-x-2 mt-1">
                        <span className="text-xs text-gray-500">{branchAccess.branch_code}</span>
                        <Badge 
                          variant="outline" 
                          className={cn('text-xs', roleColors[branchAccess.role as keyof typeof roleColors])}
                        >
                          {roleLabels[branchAccess.role as keyof typeof roleLabels]}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </div>
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>
      
      {currentBranch && (
        <div className="p-3 bg-gray-50 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-sm">{currentBranch.name}</h4>
              <p className="text-xs text-gray-500">{currentBranch.address}, {currentBranch.city}</p>
            </div>
            <Badge variant="outline">{currentBranch.code}</Badge>
          </div>
        </div>
      )}
    </div>
  );
}